import React, { useEffect, useState } from "react";
import { Share2, Download, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

/**
 * Shared VS ROBOT share-rank dialog. Fetches a 1080×1080 PNG card from the
 * backend (`GET /vs-robot/share-card.png`) and offers Instagram/WhatsApp/Twitter
 * /Facebook/Download/Copy actions.
 *
 * Props:
 *  - playerName, robotId, robotName  → identifies the duel
 *  - rank, totalPlayers              → context for the caption + headline
 *  - onClose                         → close handler
 */
export default function RankShareDialog({ playerName, robotId, robotName, rank, totalPlayers, onClose }) {
  const { t, lang } = useI18n();
  const [pngBlob, setPngBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.get("/vs-robot/share-card.png", {
          params: {
            player_name: playerName,
            robot_id: robotId,
            rank,
            total_players: totalPlayers,
            lang,
          },
          responseType: "blob",
        });
        if (!active) return;
        setPngBlob(res.data);
        setPreviewUrl(URL.createObjectURL(res.data));
      } catch (e) {
        // The axios call uses `responseType: "blob"` so error bodies are
        // Blob objects — decode manually to surface the real reason to the
        // player instead of the generic message.
        let msg = t("share_error_generate");
        try {
          const errBody = e?.response?.data;
          if (errBody instanceof Blob) {
            const text = await errBody.text();
            try {
              const parsed = JSON.parse(text);
              if (parsed?.detail) {
                msg = typeof parsed.detail === "string" ? parsed.detail : JSON.stringify(parsed.detail);
              } else if (text) {
                msg = `${msg} (${text.slice(0, 140)})`;
              }
            } catch {
              // Non-JSON body (HTML from proxy, empty…) — include first chars.
              if (text) msg = `${msg} (${text.slice(0, 140)})`;
            }
          } else if (typeof errBody === "object" && errBody?.detail) {
            msg = errBody.detail;
          } else if (e?.message) {
            msg = `${msg} (${e.message})`;
          }
        } catch { /* fall through to default */ }
        toast.error(msg);
        onClose();
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [playerName, robotId, rank, totalPlayers, onClose, lang, t]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  // Robot-name is passed as a prop; keep a safe fallback so the pre-filled
  // caption never renders "…derrotar o undefined" if the name is missing.
  const safeRobotName = (robotName || t("share_robot_fallback")).toString().trim();
  const text = t("share_caption", { robot: safeRobotName.toUpperCase(), rank, total: totalPlayers });
  const url = typeof window !== "undefined" ? window.location.origin + "/vs-robot" : "";

  const downloadPng = () => {
    if (!previewUrl) return;
    const a = document.createElement("a");
    a.href = previewUrl;
    const safe = String(playerName || "jogador").replace(/[^A-Za-z0-9_.-]+/g, "_");
    a.download = `vs-robot-rank-${rank}-${safe}.png`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success(t("share_toast_downloaded"));
  };
  const copyImage = async () => {
    try {
      if (navigator.clipboard && window.ClipboardItem && pngBlob) {
        await navigator.clipboard.write([new ClipboardItem({ "image/png": pngBlob })]);
        toast.success(t("share_toast_copied_image"));
      } else { throw new Error("clipboard unsupported"); }
    } catch (e) {
      toast.error(t("share_toast_copy_image_unsupported"));
    }
  };
  const copyCaption = () => {
    if (!navigator.clipboard) { toast.error(t("share_toast_copy_unsupported")); return; }
    navigator.clipboard.writeText(`${text} ${url}`).then(
      () => toast.success(t("share_toast_copied_caption")),
      () => toast.error(t("share_toast_copy_failed"))
    );
  };
  const nativeShare = async () => {
    if (!pngBlob) return;
    try {
      const file = new File([pngBlob], `vs-robot-rank-${rank}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: `${text} ${url}` });
      } else {
        downloadPng();
      }
    } catch (e) { /* user cancelled */ }
  };
  const openWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, "_blank", "noopener,noreferrer");
  const openX = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, "_blank", "noopener,noreferrer");
  const openFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");

  return (
    <div
      className="fixed inset-0 z-[90] bg-slate-900/75 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
      data-testid="vsr-share-dialog"
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-amber-500 to-rose-500 text-white">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            <h2 className="font-[Fredoka] text-lg font-bold">{t("share_title", { rank })}</h2>
          </div>
          <button onClick={onClose} className="hover:bg-white/15 rounded-full p-1.5" data-testid="vsr-share-close">✕</button>
        </div>
        <div className="p-5">
          {loading ? (
            <div className="aspect-square bg-slate-100 rounded-xl grid place-items-center text-slate-400 text-sm" data-testid="vsr-share-loading">
              {t("share_generating")}
            </div>
          ) : (
            <img
              src={previewUrl} alt={t("share_image_alt")}
              className="w-full rounded-xl border border-slate-200 shadow-sm bg-slate-100"
              style={{ aspectRatio: "1 / 1" }}
              data-testid="vsr-share-preview"
            />
          )}
          <p className="text-xs text-slate-500 mt-3 mb-3" dangerouslySetInnerHTML={{ __html: t("share_hint_html") }} />
          <div className="grid grid-cols-2 gap-2">
            <button onClick={downloadPng} disabled={!previewUrl} className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-full bg-pink-500 hover:bg-pink-600 disabled:opacity-50 text-white font-bold text-sm transition" data-testid="vsr-share-download">
              <Download className="w-4 h-4" /> {t("share_btn_download")}
            </button>
            <button onClick={copyImage} disabled={!pngBlob} className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-full bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white font-bold text-sm transition" data-testid="vsr-share-copy-image">
              <Copy className="w-4 h-4" /> {t("share_btn_copy_image")}
            </button>
            <button onClick={openWhatsApp} className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm transition" data-testid="vsr-share-whatsapp">
              WhatsApp
            </button>
            <button onClick={openX} className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition" data-testid="vsr-share-twitter">
              X / Twitter
            </button>
            <button onClick={openFacebook} className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm transition" data-testid="vsr-share-facebook">
              Facebook
            </button>
            <button onClick={copyCaption} className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-900 font-bold text-sm transition" data-testid="vsr-share-copy-caption">
              {t("share_btn_copy_caption")}
            </button>
          </div>
          {typeof navigator !== "undefined" && navigator.canShare && (
            <button onClick={nativeShare} className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm transition" data-testid="vsr-share-native">
              <Share2 className="w-4 h-4" /> {t("share_btn_native")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
